import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  getSearchKeywords,
  createCompanyInKS,
} from "../../../../services/accIqService";
import "./NewsSelector.css";

const NewsSelector = ({
  selectedCompanies,
  onCompaniesChange,
  disabled,
  vendorCompanyId = "",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const [isCreating, setIsCreating] = useState(false);

  // Inline website URL states
  const [showWebsiteInput, setShowWebsiteInput] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [isValidatingUrl, setIsValidatingUrl] = useState(false);
  const [urlError, setUrlError] = useState("");
  const [companyNameToCreate, setCompanyNameToCreate] = useState("");

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
        // Reset inline form when clicking outside
        if (showWebsiteInput) {
          handleCancelCreate();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showWebsiteInput]);

  // Search companies
  const searchCompanies = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    setShowDropdown(true);
    // Reset inline form when searching
    setShowWebsiteInput(false);
    try {
      console.log("Searching news for:", query);
      const response = await getSearchKeywords(query);
      console.log("News API results:", response);
      // Ensure results is an array (handle nested data structure)
      const results = Array.isArray(response) ? response : response?.data || [];
      // Filter only company types
      const companyResults = results.filter((item) => item.type === "company");
      console.log("Filtered company results:", companyResults);
      setSuggestions(companyResults);
    } catch (err) {
      console.error("News search error:", err);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Filter out special characters from input
  const filterSpecialCharacters = (value) => {
    // Remove special characters like @, +, #, $, %, ^, &, *, =, etc.
    // Allow only letters, numbers, spaces, hyphens, and periods
    // return value.replace(/[^a-zA-Z0-9\s\-\.]/g, '');
    return value.replace(/[^a-zA-Z0-9\s.,&\-_'"]/g, "");
  };

  const handleInputChange = (e) => {
    const rawValue = e.target.value;
    const newValue = filterSpecialCharacters(rawValue);
    setInputValue(newValue);

    // Debounce API call
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchCompanies(newValue);
    }, 300);
  };

  const handleSelect = (company) => {
    // Check if already selected
    const isAlreadySelected = selectedCompanies.some(
      (c) => c.value === company.value
    );

    if (!isAlreadySelected) {
      onCompaniesChange([...selectedCompanies, company]);
    }

    setInputValue("");
    setSuggestions([]);
    setShowDropdown(false);
  };

  const handleRemove = (companyValue) => {
    onCompaniesChange(
      selectedCompanies.filter((c) => c.value !== companyValue)
    );
  };

  const handleFocus = () => {
    if (suggestions.length > 0 || showWebsiteInput) {
      setShowDropdown(true);
    }
  };

  // Show inline website URL input when Create is clicked
  const handleCreateClick = () => {
    if (!inputValue || inputValue.length < 2) return;
    // Don't allow create if no financial company selected
    if (!vendorCompanyId) return;

    setCompanyNameToCreate(inputValue);
    setWebsiteUrl("");
    setUrlError("");
    setShowWebsiteInput(true);
  };

  // Cancel and reset inline form
  const handleCancelCreate = () => {
    setShowWebsiteInput(false);
    setWebsiteUrl("");
    setUrlError("");
    setCompanyNameToCreate("");
    setIsValidatingUrl(false);
  };

  // Validate URL format
  const isValidUrlFormat = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === "http:" || urlObj.protocol === "https:";
    } catch {
      return false;
    }
  };

  // Validate website URL and create company
  const handleSubmitWebsite = async () => {
    setUrlError("");

    // Basic URL format validation
    if (!websiteUrl.trim()) {
      setUrlError("Please enter a website URL");
      return;
    }

    if (!isValidUrlFormat(websiteUrl.trim())) {
      setUrlError("Please enter a valid URL (e.g., https://example.com)");
      return;
    }

    setIsValidatingUrl(true);

    try {
      // Validate URL by making a HEAD request
      // Using no-cors mode due to CORS restrictions - we can only check if the request fails
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      try {
        await fetch(websiteUrl.trim(), {
          method: "HEAD",
          mode: "no-cors",
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === "AbortError") {
          setUrlError(
            "Website validation timed out. Please check the URL and try again."
          );
        } else {
          setUrlError(
            "Website is not reachable or returned an invalid response"
          );
        }
        setIsValidatingUrl(false);
        return;
      }

      // URL is reachable, proceed with company creation
      setIsCreating(true);
      console.log(
        "Creating company:",
        companyNameToCreate,
        "with website:",
        websiteUrl
      );

      const ksResponse = await createCompanyInKS({
        name: companyNameToCreate,
        vendor_company_id: vendorCompanyId,
        website: websiteUrl.trim(),
      });
      console.log("KS API response:", ksResponse);

      // Success - clear input and reset form
      // alert(`Company "${companyNameToCreate}" created successfully!`);

      // Build company object in SAME format as search results
      const newlyCreatedCompany = {
        value: ksResponse.companyid || ksResponse.id,
        label: companyNameToCreate,
        type: "company",
      };

      // Add to selected companies (avoid duplicates just in case)
      onCompaniesChange([...selectedCompanies, newlyCreatedCompany]);

      // Reset UI
      setInputValue("");
      setSuggestions([]);
      setShowDropdown(false);
      handleCancelCreate();
    } catch (error) {
      console.error("Error creating company:", error);
      setUrlError("Failed to create company. Please try again.");
    } finally {
      setIsValidatingUrl(false);
      setIsCreating(false);
    }
  };

  const isSelected = (company) => {
    return selectedCompanies.some((c) => c.value === company.value);
  };

  return (
    <div className="news-selector">
      <label className="news-selector-label">Select companies for tracking news</label>

      <div className="news-selector-input-wrapper" ref={wrapperRef}>
        <input
          type="text"
          className="news-selector-input"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder="Search for companies..."
          disabled={disabled || showWebsiteInput}
        />

        {showDropdown && (
          <div className="news-selector-dropdown">
            {showWebsiteInput ? (
              // Inline website URL form
              <div className="news-selector-create-form">
                <div className="news-selector-create-header">
                  Create: <strong>{companyNameToCreate}</strong>
                </div>
                <div className="news-selector-create-body">
                  <label className="company-website-label">
                    Company Website:
                  </label>
                  <input
                    type="text"
                    className={`news-selector-create-input ${
                      urlError ? "error" : ""
                    }`}
                    value={websiteUrl}
                    onChange={(e) => {
                      setWebsiteUrl(e.target.value);
                      setUrlError("");
                    }}
                    placeholder="https://example.com"
                    disabled={isValidatingUrl || isCreating}
                    autoFocus
                  />
                  {urlError && (
                    <div className="news-selector-create-error">{urlError}</div>
                  )}
                </div>
                <div className="news-selector-create-actions">
                  <button
                    type="button"
                    className="news-selector-cancel-btn"
                    onClick={handleCancelCreate}
                    disabled={isValidatingUrl || isCreating}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="news-selector-submit-btn"
                    onClick={handleSubmitWebsite}
                    disabled={isValidatingUrl || isCreating}
                  >
                    {isValidatingUrl
                      ? "Validating..."
                      : isCreating
                      ? "Creating..."
                      : "Submit"}
                  </button>
                </div>
              </div>
            ) : isLoading ? (
              <div className="news-selector-loading">Searching...</div>
            ) : suggestions.length > 0 ? (
              suggestions.map((company) => (
                <div
                  key={company.value}
                  className={`news-selector-dropdown-item ${
                    isSelected(company) ? "selected" : ""
                  }`}
                  onClick={() => handleSelect(company)}
                >
                  <span>{company.label}</span>
                  <span className="news-selector-dropdown-item-type">
                    company
                  </span>
                </div>
              ))
            ) : inputValue.length >= 2 ? (
              <div className="news-selector-no-results">
                <span className="news-selector-no-results-text">
                  No companies found
                </span>
                <button
                  type="button"
                  className="news-selector-create-btn"
                  onClick={handleCreateClick}
                  disabled={disabled || !vendorCompanyId}
                  title={
                    !vendorCompanyId
                      ? "Please select a financial company first"
                      : ""
                  }
                >
                  Create
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {selectedCompanies.length > 0 && (
        <div className="news-selector-chips">
          {selectedCompanies.map((company) => (
            <div key={company.value} className="news-selector-chip">
              <span>{company.label}</span>
              <button
                type="button"
                className="news-selector-chip-remove"
                onClick={() => handleRemove(company.value)}
                disabled={disabled}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="news-selector-hint">
        Select multiple companies for news processing
      </p>
    </div>
  );
};

export default NewsSelector;
